import "phoenix_html";
import * as Hooks from "phenix_storybook/hooks";
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { displayToast } from "./app/toast";
import { initSentry } from "./helpers/sentry-config.js";
import { DiagnosticHook } from "./hooks/diagnostic_hook";
import { OfflineModeHook } from "./hooks/offline_mode_hook";
import { TroubleshootingHook } from "./hooks/troubleshooting_hook";
import offlineStore from "./lib/offline_store";
import { setupPerformanceObserver } from "./lib/performance_observer";
import * as logger from "./logger";
import { Domain } from "./logger";
import storage from "./storage";
import { S3Uploader } from "./uploaders/s3_uploader";

// Values replaced in Dockerfile
const GIT_REVISION = "b03c1959aa86923d0e294672e842ff6215643a12";
const CACHE_NAME = `phenix-field-${GIT_REVISION}`;

const uploaders = { S3Uploader };

const sentryEnabled = document.body.dataset.sentryEnabled === "true";
const sentryEnv = document.body.dataset.sentryAppEnv;

storage.setItem({ key: "sentryEnabled", value: sentryEnabled }, { storeName: "main" });
storage.setItem({ key: "sentryEnv", value: sentryEnv }, { storeName: "main" });
initSentry(sentryEnabled, sentryEnv, GIT_REVISION);

offlineStore.init();
window.offlineModeStore = offlineStore;

/*
 *  LiveView setup
 */
const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

const liveSocket = new LiveSocket("/live", Socket, {
  params: {
    _csrf_token: csrfToken,
  },
  hooks: { ...Hooks, DiagnosticHook, OfflineModeHook, TroubleshootingHook },
  uploaders: uploaders,
});
liveSocket.connect();

window.liveSocket = liveSocket;

/*
 *  Performance observer setup
 */
setupPerformanceObserver();

/*
 * Service Worker registration & communication
 */

(async function main() {
  if ("serviceWorker" in navigator) {
    try {
      // Register the service worker
      const registration = await navigator.serviceWorker.register("/assets/sw.js", {
        scope: "/",
      });

      // Immediate update check
      await registration.update();

      // Set up periodic updates
      setInterval(
        async () => {
          logger.debug(Domain.App, "checking for sw update");
          await registration.update();
        },
        5 * 60 * 1000,
      ); // Check every 5mn

      // Handle updates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "activated" && navigator.serviceWorker.controller) {
            logger.debug(Domain.App, "service worker updated");
          }
        });
      });

      await navigator.serviceWorker.ready;

      offlineStore.init();
    } catch (error) {
      logger.error(Domain.App, `Registration failed with ${error}`);
      Sentry.captureException(error);
    }

    setInterval(() => {
      sendMessage({ type: "flush_forms" });
    }, 60_000);

    const appContainer = document.getElementById("app-container");
    if (appContainer) {
      const userId = appContainer.dataset.userId;
      storage.setItem({ key: "user_id", value: userId }, { storeName: "main" });
      const criticalAssetsMessage = { type: "cache_assets", userId: userId, group: "critical" };
      setTimeout(() => {
        sendMessage(criticalAssetsMessage);
      }, 5_000);

      const taskAssetsMessage = { type: "cache_assets", userId: userId, group: "tasks" };
      setTimeout(() => {
        sendMessage(taskAssetsMessage);
      }, 10_000);

      const refreshAssetsMessage = { type: "cache_assets", userId: userId };
      setInterval(() => {
        sendMessage(refreshAssetsMessage);
      }, 300_000);
    }
  }

  storage.clearExpiredNamespaces();
})();

function sendMessage(message) {
  navigator.serviceWorker.controller.postMessage(JSON.stringify(message));
}

/*
 * Toast messages
 */

const toastChannel = new BroadcastChannel("toast-channel");
toastChannel.onmessage = (event) => {
  displayToast(event.data.message, event.data.kind);
};

/*
 * Function used to generate url and skip memory cache
 */

function skipMemoryCacheUrl(path, hash) {
  const url = hash ? `${path}?ts=${Date.now()}${hash}` : `${path}?ts=${Date.now()}`;
  return url;
}

/*
 * Custom iframe events
 */

// sent by inner iframe in form_html.ex#toggleBackLink
window.document.addEventListener("toggle_back_link", () => {
  const el = document.getElementById("form-back-link");
  if (el.style.display === "none") {
    el.style.display = "block";
  } else {
    el.style.display = "none";
  }
});

// sent by inner iframe in form_html.ex#submitForm
window.document.addEventListener("form_submit", async (event) => {
  const formData = event.detail;
  const now = new Date().toISOString();
  const key = `task-${formData.task_id}`;
  const redirectUrl = formData.place_id ? `/places/${formData.place_id}` : "/home";

  formData.key = key;
  formData.submitted_at = now;

  displayToast("<b>⚙︎</b> Formulaire en cours de traitement...", "success", () => {
    window.location.assign(redirectUrl);
  });

  try {
    const geolocState = await storage.getItem("geolocPermissionState", {
      storeName: "main",
    });

    if (geolocState?.position) {
      formData.position = geolocState.position;
    }
    const existingItem = await storage.getItem(key, {
      storeName: "forms",
    });
    if (!existingItem || existingItem.status !== "processing") {
      await storage.setItem(formData, {
        storeName: "forms",
      });

      sendMessage({
        type: "submit_form",
        formKey: key,
      });
    }
  } catch (error) {
    logger.error(Domain.App, "cannot save submitted form", formData, error);
    Sentry.captureException(error, { extra: { formData: formData } });
  }
});

// function used to hide loading feedback after page load
window.document.addEventListener("alpine:initialized", () => {
  const el = document.querySelector("#network-feedback");
  if (el?._x_dataStack?.[0]) el._x_dataStack[0].hide("network");
});

// function used to show loading feedback after redirect events
window.addEventListener("beforeunload", () => {
  const el = document.querySelector("#network-feedback");
  if (el?._x_dataStack?.[0]) {
    el._x_dataStack[0].showAfter("network", 800);
  }
});

// function used to handle alpine errors from form iframe
window.addEventListener("message", (event) => {
  if (event.data?.type === "AlpineJS-error") {
    Sentry.captureException(new Error(`${event.data.type} : ${event.data.error}`), {
      extra: { state: event.data.state },
    });
  }
});

window.navigate = async (path, $data) => {
  path = path.startsWith("http") ? new URL(path).pathname : path;
  const logPrefix = `Navigating to ${path}: `;
  if (offlineStore.value || !navigator.onLine) {
    logger.debug(
      Domain.App,
      `${logPrefix}offline mode is enabled, let the service worker handle this`,
    );
    window.location.href = path;
    return;
  }

  $data.globalLoader = { path: path, error: $data.globalLoader?.error, loading: true };
  const asset = await storage.getItem(path, { storeName: "assets" });
  if (asset?.updatedAt) {
    logger.debug(Domain.App, `${logPrefix}known updatedAt value: ${asset.updatedAt}`);

    try {
      const response = await fetch(path, {
        headers: { "If-Modified-Since": asset.updatedAt },
        signal: AbortSignal.timeout(5000),
      });
      if (response.redirected) {
        logger.debug(Domain.App, `${logPrefix}server redirected to ${response.url}`);
        $data.globalLoader = null;
        window.location.href = response.url;
      } else if (response.status === 304) {
        logger.debug(
          Domain.App,
          `${logPrefix}cache is up-to-date, will navigate to ${path}#cache_first`,
        );

        $data.globalLoader = null;
        window.location.href = skipMemoryCacheUrl(path, "#cache_first");
      } else if (response.ok) {
        logger.debug(Domain.App, `${logPrefix}cache is not up-to-date, will refresh it`);

        const responseClone = response.clone();
        await response.text();

        logger.debug(Domain.App, `${logPrefix}finished receiving response, will cache it`);

        const cache = await caches.open(CACHE_NAME);
        await cache.put(path, responseClone);
        asset.updatedAt = new Date(response.headers.get("Last-Modified"));
        await storage.setItem(asset, { storeName: "assets" });

        logger.debug(Domain.App, `${logPrefix}will navigate to ${path}#cache_first`);

        $data.globalLoader = null;
        window.location.href = skipMemoryCacheUrl(path, "#cache_first");
      } else {
        logger.debug(
          Domain.App,
          `${logPrefix} server error (received a ${response.status} response)`,
        );
        $data.globalLoader.error = "server_error";
        $data.globalLoader.loading = false;
      }
    } catch (error) {
      if (error.name === "TimeoutError" || error.name === "AbortError") {
        logger.debug(Domain.App, `${logPrefix}timeout`);
        $data.globalLoader.error = "timeout";
        $data.globalLoader.loading = false;
      } else {
        logger.debug(Domain.App, `${logPrefix}other network error (${error.name})`);
        $data.globalLoader.error = "network_error";
        $data.globalLoader.loading = false;
      }
    }
  } else {
    logger.debug(Domain.App, `${logPrefix}path not in cache, will navigate to it directly`);
    $data.globalLoader = null;
    window.location.href = skipMemoryCacheUrl(path);
  }
};

window.activateOffline = (path) => {
  logger.debug(
    Domain.App,
    `user chose to activate offline mode, will switch to offline mode and navigate to ${path}`,
  );
};

window.deleteEmptyFormData = (taskId) => {
  storage.deleteEmptyFormData(taskId);
};
